import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase/config';
import {
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  collection,
  query,
  getDocs,
} from 'firebase/firestore';

const SuperAdminSetup = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    password: '',
    confirmPassword: '',
    schoolName: '',
    schoolAddress: '',
    schoolPhone: '',
  });

  const steps = [
    'Création du compte Super Admin',
    'Configuration de l\'établissement',
    'Finalisation',
  ];

  useEffect(() => {
    checkInitialSetup();
  }, []);

  const checkInitialSetup = async () => {
    try {
      // Vérifier si un super admin existe déjà
      const superAdminQuery = query(collection(db, 'users'));
      const querySnapshot = await getDocs(superAdminQuery);
      
      if (!querySnapshot.empty) {
        // Si des utilisateurs existent déjà, rediriger vers la page de connexion
        navigate('/login');
        return;
      }
      
      setLoading(false);
    } catch (error) {
      setError('Erreur lors de la vérification de la configuration initiale');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateStep = () => {
    setError('');

    switch (activeStep) {
      case 0:
        if (!formData.nom || !formData.prenom || !formData.email || !formData.password || !formData.confirmPassword) {
          setError('Veuillez remplir tous les champs');
          return false;
        }
        if (formData.password !== formData.confirmPassword) {
          setError('Les mots de passe ne correspondent pas');
          return false;
        }
        if (formData.password.length < 6) {
          setError('Le mot de passe doit contenir au moins 6 caractères');
          return false;
        }
        break;
      case 1:
        if (!formData.schoolName || !formData.schoolAddress || !formData.schoolPhone) {
          setError('Veuillez remplir tous les champs');
          return false;
        }
        break;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) return;

    if (activeStep === steps.length - 1) {
      await finalizeSetup();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const finalizeSetup = async () => {
    setLoading(true);
    try {
      
      // 1. Créer le compte Super Admin dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // 2. Mettre à jour le profil utilisateur
      await updateProfile(userCredential.user, {
        displayName: `${formData.prenom} ${formData.nom}`,
      });

      // 3. Créer le document utilisateur dans Firestore
      const userData = {
        uid: userCredential.user.uid,
        nom: formData.nom,
        prenom: formData.prenom,
        email: formData.email,
        role: 'superAdmin',
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      const userDocRef = doc(db, 'users', userCredential.user.uid);
      await setDoc(userDocRef, userData);
      
      // Vérifier que le document a bien été créé
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
      } else {
        throw new Error('Le document utilisateur n\'a pas été créé');
      }

      // 4. Créer l'établissement principal
      const establishmentData = {
        nom: formData.schoolName,
        adresse: formData.schoolAddress,
        telephone: formData.schoolPhone,
        isMainEstablishment: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      const establishmentRef = doc(collection(db, 'establishments'));
      await setDoc(establishmentRef, establishmentData);

      // 5. Créer les paramètres globaux
      const settingsData = {
        schoolName: formData.schoolName,
        enableNotifications: true,
        enableMessaging: true,
        maxAbsenceDays: 30,
        maxLeaveRequests: 5,
        emailNotifications: true,
        autoApproveLeave: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      await setDoc(doc(db, 'settings', 'global'), settingsData);

      // Rediriger vers le tableau de bord
      navigate('/dashboard');
    } catch (error) {
      console.error('=== Erreur lors de la configuration ===');
      console.error('Type d\'erreur:', error.name);
      console.error('Message:', error.message);
      console.error('Stack:', error.stack);
      setError('Erreur lors de la configuration : ' + error.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Prénom"
              name="prenom"
              value={formData.prenom}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Nom"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Mot de passe"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Confirmer le mot de passe"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              margin="normal"
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Nom de l'établissement"
              name="schoolName"
              value={formData.schoolName}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Adresse"
              name="schoolAddress"
              value={formData.schoolAddress}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={2}
            />
            <TextField
              fullWidth
              label="Téléphone"
              name="schoolPhone"
              value={formData.schoolPhone}
              onChange={handleChange}
              margin="normal"
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Vérifiez les informations suivantes avant de finaliser la configuration :
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Super Admin : {formData.prenom} {formData.nom}
              <br />
              Email : {formData.email}
              <br />
              Établissement : {formData.schoolName}
              <br />
              Adresse : {formData.schoolAddress}
              <br />
              Téléphone : {formData.schoolPhone}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom align="center">
          Configuration initiale
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          {activeStep !== 0 && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Retour
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleNext}
          >
            {activeStep === steps.length - 1 ? 'Finaliser' : 'Suivant'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SuperAdminSetup;
